// @ts-strict-ignore
import { trackQuickStartCIEvent } from './../../../actions/trackEvent';
import { useDispatch, useSelector } from 'react-redux';
import { ScanConfirmation } from './../steps/ScanConfirmation';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { createNewAppQuickStartCIRoute, createQuickStartCIStepRoute } from '../../../helpers/routeHelpers';
import { VCSIntegration } from '../steps/VCSIntegration';
import * as AnalyticEvents from '../../../utils/analytics';
import {
    QuickStartCISetupStep,
    QuickStartCISetupStepType,
    SetupRouteComponentMatchParams,
} from './../../../typescriptHelpers';
import { VCSSetup } from '../steps/VCSSetup';
import { AppStoreIntegration } from '../steps/AppStoreIntegration';
import { ScanDetails } from '../steps/ScanDetails';
import { PlatformConfirmation } from '../steps/PlatformConfirmation';
import { RootState } from '../../../reducers/reducers';
import { useEffect } from 'react';
import { DownloadFiles } from '../steps/DownloadFiles';
import { UploadSecrets } from '../steps/UplodadSecrets';
import { WhatIsQuickStartCI } from '../steps/WhatIsQuickstartCI';

export const SETUP_STEP_BY_TYPE: Record<QuickStartCISetupStepType, QuickStartCISetupStep> = {
    [QuickStartCISetupStepType.WhatIsQuickStartCI]: WhatIsQuickStartCI,
    [QuickStartCISetupStepType.PlatformConfirmation]: PlatformConfirmation,
    [QuickStartCISetupStepType.VCSIntegration]: VCSIntegration,
    [QuickStartCISetupStepType.VCSSettings]: VCSSetup,
    [QuickStartCISetupStepType.AppStoreIntegration]: AppStoreIntegration,
    [QuickStartCISetupStepType.ScanDetails]: ScanDetails,
    [QuickStartCISetupStepType.ScanDetailsConfirmation]: ScanConfirmation,
    [QuickStartCISetupStepType.UploadSecrets]: UploadSecrets,
    [QuickStartCISetupStepType.DownloadFiles]: DownloadFiles,
};

export const NORMAL_APP_SEQUENCE = [
    QuickStartCISetupStepType.WhatIsQuickStartCI,
    QuickStartCISetupStepType.VCSIntegration,
    QuickStartCISetupStepType.VCSSettings,
    QuickStartCISetupStepType.AppStoreIntegration,
    QuickStartCISetupStepType.ScanDetails,
    QuickStartCISetupStepType.ScanDetailsConfirmation,
    QuickStartCISetupStepType.UploadSecrets,
    QuickStartCISetupStepType.DownloadFiles,
];

export const NO_APP_SEQUENCE = [
    QuickStartCISetupStepType.WhatIsQuickStartCI,
    QuickStartCISetupStepType.PlatformConfirmation,
    ...NORMAL_APP_SEQUENCE.slice(1),
];
interface QuickStartCISetupStepHookData {
    currentStep: number;
    stepCount: number;
    currentSetupStep: QuickStartCISetupStep;
    hasPreviousStep: boolean;
    hasNextStep: boolean;
    goToNextStep: () => void;
    goToPreviousStep: () => void;
}

export const useFirstIncompleteQuickStartCIStep = (
    appId: string | undefined | null,
    orgId: string,
    stepsSequence: QuickStartCISetupStepType[] = NORMAL_APP_SEQUENCE,
): [number, number] => {
    const firstIncompleteStepIndex = useSelector((state: RootState) => {
        for (let i = 0; i < stepsSequence.length; i++) {
            const step = stepsSequence[i];
            const { isCompleteSelector } = SETUP_STEP_BY_TYPE[step] ?? {};
            if (isCompleteSelector && !isCompleteSelector(state, { appId, orgId })) {
                return i;
            }
        }
        return stepsSequence.length - 1;
    });

    return [firstIncompleteStepIndex, NORMAL_APP_SEQUENCE.length];
};

export const useQuickStartCISteps: (
    appId: string | undefined | null,
    orgId: string,
) => QuickStartCISetupStepHookData = (appId, orgId) => {
    const stepsSequence = appId ? NORMAL_APP_SEQUENCE : NO_APP_SEQUENCE;
    const dispatch = useDispatch();

    const match = useRouteMatch<SetupRouteComponentMatchParams>();
    const { setupTypeParam } = match.params;
    const setupStepTypeIndex = stepsSequence.findIndex((step) => step === setupTypeParam);
    const setupStepType =
        setupStepTypeIndex > -1 ? (stepsSequence[setupStepTypeIndex] as QuickStartCISetupStepType) : undefined;
    const history = useHistory();

    const [firstIncompleteStepIndex, stepCount] = useFirstIncompleteQuickStartCIStep(appId, orgId, stepsSequence);

    const createRouteFunction = (orgIdParam: string, appIdParam: string, setupTypeParam: string) => {
        return appId ?
                createQuickStartCIStepRoute(orgIdParam, appIdParam, setupTypeParam)
            :   createNewAppQuickStartCIRoute(orgIdParam, setupTypeParam);
    };

    const currentSetupStep = SETUP_STEP_BY_TYPE[setupStepType ?? NORMAL_APP_SEQUENCE[0]];

    useEffect(() => {
        if (setupStepType) {
            dispatch(
                trackQuickStartCIEvent({
                    orgId,
                    appId,
                    eventName: AnalyticEvents.Event.ViewQuickStartCIStep,
                    extraData: {
                        stepType: setupStepType,
                    },
                }),
            );
        }
    }, [setupStepType]);

    useEffect(() => {
        const { appIdParam, orgIdParam } = match.params;
        if (!setupStepType) {
            const firstStepUrl = createRouteFunction(orgIdParam, appIdParam, NORMAL_APP_SEQUENCE[0]);
            history.replace(firstStepUrl);
        } else if (setupStepTypeIndex > firstIncompleteStepIndex) {
            const firstIncompleteStep = stepsSequence[firstIncompleteStepIndex];
            const firstIncompleteStepUrl = createRouteFunction(orgIdParam, appIdParam, firstIncompleteStep);
            history.replace(firstIncompleteStepUrl);
        }
    }, [setupStepType, setupStepTypeIndex, firstIncompleteStepIndex, appId]);

    const currentStepIndex = stepsSequence.indexOf(setupStepType);
    const hasPreviousStep = currentStepIndex > 0;
    const hasNextStep = currentStepIndex < stepsSequence.length - 1;

    const goToNextStep = () => {
        const nextStepType = stepsSequence[currentStepIndex + 1];

        const { appIdParam, orgIdParam } = match.params;
        const nextURL = createRouteFunction(orgIdParam, appIdParam, nextStepType);
        history.push(nextURL);
    };

    const goToPreviousStep = () => {
        const previousStepType = stepsSequence[currentStepIndex - 1];
        const { appIdParam, orgIdParam } = match.params;
        const previousURL = createRouteFunction(orgIdParam, appIdParam, previousStepType);
        history.push(previousURL);
    };

    return {
        currentSetupStep,
        hasPreviousStep,
        hasNextStep,
        goToNextStep,
        goToPreviousStep,
        currentStep: currentStepIndex + 1,
        stepCount,
    };
};
